import React, { useState } from 'react';
import { graphBuilderApi } from '../../services/api';
import './GraphLinksPage.css';

export default function LinkSuggestionPanel({
  knowledgeGraphId,
  processId,
  onAccept,
  onClose,
}) {
  const [instruction, setInstruction] = useState('');
  const [history, setHistory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSuggest = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await graphBuilderApi.suggestGraphLinks({
        knowledge_graph_id: knowledgeGraphId,
        process_id: processId,
        mode: history.length > 0 ? 'conversational' : 'full_scan',
        user_instruction: instruction || undefined,
        history,
      });
      setSuggestions(resp.suggestions || []);
      setSelected(new Set(resp.suggestions?.map((_, i) => i) || []));
      if (instruction) {
        setHistory((prev) => [
          ...prev,
          { role: 'user', content: instruction },
          { role: 'assistant', content: JSON.stringify(resp.suggestions?.length || 0) + ' suggestions returned' },
        ]);
        setInstruction('');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (index) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleAccept = () => {
    const accepted = suggestions.filter((_, i) => selected.has(i));
    if (accepted.length > 0) {
      onAccept(accepted);
    }
  };

  const directionLabel = (dir) => {
    if (dir === 'knowledge_to_process') return 'KG → Process';
    if (dir === 'process_to_knowledge') return 'Process → KG';
    return 'Bidirectional';
  };

  return (
    <div className="suggestion-overlay">
      <div className="suggestion-panel">
        <div className="suggestion-header">
          <h3>AI Link Suggestions</h3>
          <button className="suggestion-close" onClick={onClose}>×</button>
        </div>

        <div className="suggestion-input-area">
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Optional: describe what kinds of connections you want (e.g. 'connect compliance rules to the audit check step')..."
            rows={3}
          />
          <button
            className="suggest-run-btn"
            onClick={handleSuggest}
            disabled={loading}
          >
            {loading ? 'Analyzing...' : suggestions.length > 0 ? 'Refine Suggestions' : 'Generate Suggestions'}
          </button>
        </div>

        {error && <div className="suggestion-error">{error}</div>}

        {suggestions.length > 0 && (
          <>
            <div className="suggestion-list">
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  className={`suggestion-card ${selected.has(i) ? 'selected' : ''}`}
                  onClick={() => toggleSelection(i)}
                >
                  <div className="suggestion-card-header">
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => toggleSelection(i)}
                    />
                    <span className="suggestion-node-name">
                      {s.knowledge_node_name || s.knowledge_node_id?.slice(0, 8)}
                    </span>
                    <span className="suggestion-direction">
                      {directionLabel(s.direction)}
                    </span>
                    <span className="suggestion-node-name">
                      {s.process_node_name || s.process_node_id}
                    </span>
                    <span className="suggestion-confidence">
                      {Math.round((s.confidence || 0) * 100)}%
                    </span>
                  </div>
                  {s.purpose && (
                    <div className="suggestion-purpose">{s.purpose}</div>
                  )}
                </div>
              ))}
            </div>
            <div className="suggestion-actions">
              <button className="suggestion-select-all" onClick={() => setSelected(new Set(suggestions.map((_, i) => i)))}>
                Select All
              </button>
              <button className="suggestion-select-none" onClick={() => setSelected(new Set())}>
                Select None
              </button>
              <button
                className="suggestion-accept-btn"
                onClick={handleAccept}
                disabled={selected.size === 0}
              >
                Accept {selected.size} Link{selected.size !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}

        {!loading && suggestions.length === 0 && (
          <div className="suggestion-empty">
            Click "Generate Suggestions" to let AI analyze both graphs and recommend connections.
          </div>
        )}
      </div>
    </div>
  );
}
